<template>
	<div class="header-mobile block lg:hidden container mx-auto">
		<div class="flex justify-between items-center py-5 space-x-4">
			<div class="flex-[2_2_0%] flex gap-4">
				<LayoutSideMenu
					:menu-categories-norce="menuCategoriesNorceFormatted"
					:menu-categories="menuCategoriesFormatted"
				/>
				<UserIcon class="h-6 w-6 text-primary" @click="openAccountOnDesktop" />
			</div>
			<div class="flex-[2_2_0%] flex justify-center">
				<div class="flex justify-center items-center">
					<NuxtLink :to="localePath(`/`)">
						<NuxtImg
							:src="siteLogo?.filename"
							:alt="siteLogo?.alt || 'logo'"
							class="logo max-w-60"
							loading="eager"
							:width="siteLogoDimensions?.width"
							:height="siteLogoDimensions?.height"
						/>
					</NuxtLink>
				</div>
			</div>

			<div class="flex-[2_2_0%] flex gap-4 items-center justify-end">
				<div class="flow-root">
					<button
						class="group -m-2 p-2 flex items-center relative"
						aria-label="wishlist"
						data-testid="wishlist-button"
						@click="openWishlist"
					>
						<HeartIcon class="w-6 h-6 text-current hover:text-brand-primary" />
						<client-only>
							<span
								v-if="wishlistProducts?.ItemCount"
								class="text-[10px] font-medium text-white absolute bg-secondary rounded-full w-5 h-5 top-0 right-0 leading-5"
								:class="{
									'transition-all opacity-0 header-counter': isHomePage
								}"
							>
								{{ wishlistProducts?.ItemCount }}
							</span>
						</client-only>
					</button>
				</div>
				<!-- Cart -->
				<div class="relative cursor-pointer" @click="isSidebarOpen = true">
					<ShoppingBagIcon class="h-6 w-6 text-primary cursor-pointer" />
					<client-only>
						<span
							v-if="itemsCount > 0"
							class="text-[10px] font-medium text-white text-center absolute bg-secondary rounded-full w-5 h-5 leading-5 -top-2 -right-2"
						>
							{{ itemsCount || "" }}
						</span>
					</client-only>
				</div>
			</div>
		</div>
		<StoryblokComponent
			v-for="blok in search"
			:key="blok._uid"
			:blok="blok"
			class="mb-3"
		/>
		<LayoutSideCart />
	</div>
</template>

<script setup lang="ts">
import {
	HeartIcon,
	ShoppingBagIcon,
	UserIcon
} from "@heroicons/vue/24/outline";
import { provide, ref } from "vue";
import type { SharedModal } from "~/layers/base/components/Shared/SharedModal.vue";
import type { Search, SiteLogo } from "~/types/storyblok/storyblok";
import { storeToRefs } from "pinia";
import type {
	MenuCategories,
	MenuCategoriesNorce,
	SiteLogoDimensions
} from "~/types/components/header";

const cartStore = useCartStore();
const { loadUser, userHasSession } = useLoadUser();
const localePath = useLocalePath();
const { getUser } = storeToRefs(userStore());

const itemsCount = computed(() => cartStore.itemsCount());

const isSidebarOpen = ref(false);

provide("isSidebarOpen", isSidebarOpen);

interface Props {
	search: Search[] | undefined;
	siteLogo: SiteLogo | undefined;
	siteLogoDimensions: SiteLogoDimensions;
	menuCategoriesNorce: MenuCategoriesNorce;
	menuCategories: MenuCategories;
}

const {
	search,
	siteLogo,
	siteLogoDimensions,
	menuCategoriesNorce,
	menuCategories
} = defineProps<Props>();

const { wishlistProducts } = storeToRefs(useWishlistStore());

const openWishlist = () => {
	modal.open("AccountWishlist", {
		position: "side"
	});
};

const router = useRouter();

const modal = inject<SharedModal>("modal") as SharedModal;

const formatCategories = (categories: MenuCategories | MenuCategoriesNorce) => {
	const leftMenu = categories.left?.map((cat) => ({
		...cat,
		order: "-order-1"
	}));

	return [...leftMenu, ...categories.right];
};

const isHomePage = computed(() => router.currentRoute.value.path === "/");
const menuCategoriesFormatted = computed(() =>
	formatCategories(menuCategories)
);
const menuCategoriesNorceFormatted = computed(() =>
	formatCategories(menuCategoriesNorce)
);

const openAccountOnDesktop = async () => {
	// First check if user exists in store (from localStorage persistence)
	if (getUser.value && Object.keys(getUser.value).length > 0) {
		router.push(localePath("/account"));
	} else if (!(await userHasSession())) {
		modal.open("AccountLoginForm", {
			position: "side-left"
		});
	} else {
		await loadUser();
		router.push(localePath("/account"));
	}
};
</script>
